import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@ibsys/kit';
import type { ComboboxItem } from '@ibsys/kit';
import { useConversationsAttributes } from '@/entities/conversations';
import type { StrategistTargetAttribute } from '@/entities/strategist';

/** Значение первого комбобокса, соответствующее целевой метрике «Удержание». */
const RETENTION_OPTION = '__retention__';

type Props = {
  /** Текущая целевая метрика */
  value: StrategistTargetAttribute;
  /** Колбэк смены целевой метрики */
  onChange: (next: StrategistTargetAttribute) => void;
};

/**
 * Компактный выбор целевой метрики раздела «Стратег»: «Удержание» по умолчанию
 * либо произвольный атрибут + его значение. Список атрибутов берётся тем же
 * хуком, что и сквозные фильтры (`useConversationsAttributes`); для значений
 * используются `selectValues` выбранного атрибута.
 */
export const TargetMetricPicker: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation('strategist');
  const { data } = useConversationsAttributes({ limit: 1000 });

  // Локально выбранный путь атрибута: retention или конкретный path.
  const [selectedPath, setSelectedPath] = useState<string>(value.attributePath ?? RETENTION_OPTION);

  // Только перечислимые атрибуты (с готовым списком значений) — их можно задать как цель.
  const enumerableAttributes = useMemo(
    () =>
      (data?.attributes ?? []).filter(
        (a) => !a.isDeleted && Array.isArray(a.selectValues) && a.selectValues.length > 0,
      ),
    [data?.attributes],
  );

  const metricOptions = useMemo<ComboboxItem[]>(
    () => [
      { value: RETENTION_OPTION, label: t('target.retention') },
      ...enumerableAttributes.map((a) => ({ value: a.path, label: a.pathDescription || a.name })),
    ],
    [enumerableAttributes, t],
  );

  const selectedAttribute = useMemo(
    () => enumerableAttributes.find((a) => a.path === selectedPath),
    [enumerableAttributes, selectedPath],
  );

  const valueOptions = useMemo<ComboboxItem[]>(
    () =>
      (selectedAttribute?.selectValues ?? [])
        .filter((v) => !v.isDeleted)
        .map((v) => ({ value: v.value, label: v.value })),
    [selectedAttribute],
  );

  const handleMetricChange = (next?: string | number) => {
    const path = String(next ?? RETENTION_OPTION);
    setSelectedPath(path);
    if (path === RETENTION_OPTION) {
      onChange({ retention: true });
      return;
    }
    // Атрибут выбран, значение ещё нет — метрика неполна, пока остаёмся на удержании.
    onChange({ retention: true });
  };

  const handleValueChange = (next?: string | number) => {
    if (!selectedAttribute || next === undefined || next === null) return;
    onChange({ attributePath: selectedAttribute.path, value: String(next) });
  };

  const isRetention = selectedPath === RETENTION_OPTION;

  return (
    <div className="flex flex-1 flex-wrap items-end gap-4">
      <Combobox
        label={t('target.label')}
        options={metricOptions}
        value={selectedPath}
        onChange={handleMetricChange}
        className="min-w-60 flex-1"
      />
      {!isRetention && (
        <Combobox
          label={t('target.value')}
          options={valueOptions}
          value={value.attributePath === selectedAttribute?.path ? value.value : undefined}
          onChange={handleValueChange}
          placeholder={t('target.valuePlaceholder')}
          emptyMessage={t('target.noValues')}
          className="min-w-52 flex-1"
        />
      )}
    </div>
  );
};
